import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../../hooks/useSEO';
import { useCaseStudyIndex } from '../../hooks/useCaseStudyIndex';
import { buildBreadcrumbJsonLd } from '../../config/seo';
import { getServiceBySlug } from '../../data/services';
import { Language } from '../../routes/routeConfig';
import SectionHeading from '../../components/SectionHeading/SectionHeading';
import CaseStudyCard from '../../components/CaseStudyCard/CaseStudyCard';
import BlobDecoration from '../../components/BlobDecoration/BlobDecoration';
import styles from './CaseStudies.module.css';

const CaseStudies: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const service = slug ? getServiceBySlug(slug) : undefined;
  const { slugs: caseStudySlugs, loading } = useCaseStudyIndex(slug || '');

  const seo = useSEO({
    titleKey: 'seo.caseStudies.title',
    descriptionKey: 'seo.caseStudies.description',
    routeKey: 'caseStudies',
    routeParams: { slug },
    titleValues: { serviceName: service ? t(service.nameKey) : '' },
    descriptionValues: { serviceName: service ? t(service.nameKey) : '' },
    structuredData: service
      ? buildBreadcrumbJsonLd(
          [
            { name: t('nav.home'), routeKey: 'home' },
            { name: t('nav.services'), routeKey: 'services' },
            { name: t(service.nameKey), routeKey: 'serviceDetail', params: { slug: service.slug } },
            { name: t('nav.caseStudies'), routeKey: 'caseStudies', params: { slug: service.slug } },
          ],
          i18n.language as Language
        )
      : undefined,
  });

  if (!service) {
    return <Navigate to="/404" replace />;
  }

  if (!loading && caseStudySlugs.length === 0) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className={styles.page}>
      {seo}
      <section className={styles.header}>
        <BlobDecoration
          color={service.slug === 'rehab' ? 'yellow' : 'blue'}
          position="top-right"
          size="md"
        />
        <div className="container">
          <SectionHeading
            title={t('caseStudies.pageTitle')}
            subtitle={t('caseStudies.pageSubtitle')}
            accentColor={service.accentColor}
          />
        </div>
      </section>

      <section className={styles.grid}>
        <div className="container">
          <div className={styles.cards}>
            {caseStudySlugs.map((csSlug) => (
              <CaseStudyCard
                key={csSlug}
                slug={csSlug}
                accentColor={service.accentColor}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
